import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useStore, Panel, Badge, Empty, Modal, Field } from '../components/ui.jsx';
import { activeMembershipFor, planById, addMember, today, getState } from '../data/store.js';
import { format } from '../lib/money.js';
import { isFrozenOn } from '../lib/entitlements.js';

export default function Members() {
  const state = useStore();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [adding, setAdding] = useState(false);

  const query = q.trim().toLowerCase();
  const rows = state.members.filter((m) => {
    if (status && m.status !== status) return false;
    if (!query) return true;
    return [m.name, m.email, m.phone].filter(Boolean).some((v) => v.toLowerCase().includes(query));
  });

  return (
    <>
      <header className="topbar">
        <h1>Members</h1>
        <span className="sub">{rows.length} of {state.members.length}</span>
        <div className="spacer" />
        <div className="row">
          <input
            type="search"
            placeholder="Search name, email, phone…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ width: 240 }}
            aria-label="Search members"
          />
          <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: 130 }} aria-label="Filter by status">
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="lapsed">Lapsed</option>
            <option value="lead">Lead</option>
          </select>
          <button className="primary" onClick={() => setAdding(true)}>Add member</button>
        </div>
      </header>

      <div className="page">
        <Panel bodyless>
          {rows.length === 0 ? <Empty>No members match that search.</Empty> : (
            <table className="data">
              <thead>
                <tr>
                  <th>Name</th><th>Status</th><th>Plan</th><th>Contact</th>
                  <th>Joined</th><th className="right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((m) => {
                  const sub = activeMembershipFor(m.id);
                  const plan = sub ? planById(sub.planId) : null;
                  const frozen = sub ? isFrozenOn(sub, today()) : false;
                  return (
                    <tr key={m.id}>
                      <td>
                        <Link to={`/members/${m.id}`}>{m.name}</Link>
                        {!m.waiverSignedAt && <> <Badge tone="warn">no waiver</Badge></>}
                      </td>
                      <td><Badge status={frozen ? 'frozen' : m.status} /></td>
                      <td>
                        {plan
                          ? <>{plan.name}{sub.creditsRemaining > 0 && <span className="mono num" style={{ color: 'var(--ink-3)' }}> · {sub.creditsRemaining} left</span>}</>
                          : <span style={{ color: 'var(--ink-3)' }}>—</span>}
                      </td>
                      <td className="mono" style={{ fontSize: 12, color: 'var(--ink-2)' }}>{m.email}</td>
                      <td className="mono num" style={{ color: 'var(--ink-3)' }}>{m.joinedOn}</td>
                      <td className="right mono num">
                        {sub?.pastDueCents
                          ? <Badge tone="bad">{format(sub.pastDueCents)}</Badge>
                          : <span style={{ color: 'var(--ink-3)' }}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Panel>
      </div>

      {adding && <AddMember onClose={() => setAdding(false)} />}
    </>
  );
}

// Deliberately permissive. The job is to catch a typo like "sam@gmail" before
// a welcome email bounces, not to adjudicate RFC 5322.
const LOOKS_LIKE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function AddMember({ onClose }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const email = form.email.trim();
  const badEmail = email.length > 0 && !LOOKS_LIKE_EMAIL.test(email);
  // A warning, not a block: families share an address, and a gym that cannot
  // sign up a second family member is broken in a worse way.
  const duplicate = email && getState().members.find(
    (m) => (m.email || '').toLowerCase() === email.toLowerCase(),
  );

  return (
    <Modal
      title="Add member"
      onClose={onClose}
      footer={(
        <button
          className="primary"
          disabled={!form.name.trim() || badEmail}
          onClick={() => { addMember(form); onClose(); }}
        >
          Create
        </button>
      )}
    >
      <div className="form-grid">
        <Field label="Full name"><input value={form.name} onChange={set('name')} autoFocus /></Field>
        <Field label="Email"><input type="email" value={form.email} onChange={set('email')} /></Field>
        <Field label="Phone"><input value={form.phone} onChange={set('phone')} /></Field>
      </div>

      {badEmail && (
        <div className="notice bad">
          That does not look like an email address. Fix it or leave it blank.
        </div>
      )}
      {!badEmail && duplicate && (
        <div className="notice warn">
          {duplicate.name} already uses that address. Carry on if they share
          one — otherwise you may be adding the same person twice.
        </div>
      )}

      <p style={{ color: 'var(--ink-3)', fontSize: 12.5, margin: '10px 0 0' }}>
        A plan can be assigned from the member's page after they are created.
      </p>
    </Modal>
  );
}
